//packages block
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
// component block
import { Alert } from "../common/Alert";
import { CustomController } from "../common/CustomController";
// other packages
import { useResetPasswordMutation } from "../../../generated";
import { AUTH_LINKS, AUTH_TOKEN, BACK_TO_LOGIN, FOLLOW_INSTRUCTIONS, FORBIDDEN_EXCEPTION, RESET_PASSWORD, RESET_PASSWORD_FAILURE, RESET_PASSWORD_SUCCESS, ROOT_ROUTE, SET_PASSWORD_FIELDS, TOKEN_INVALID } from "../../constants";
import { SetPasswordInput, TokenProps } from "../../interfaceTypes";
import { resetPasswordValidationSchema } from "../../validationSchema";

export const ResetPasswordForm: FC<TokenProps> = ({ token }): JSX.Element => {
  const navigate = useNavigate()

  const methods = useForm({
    mode: "all",
    resolver: yupResolver(resetPasswordValidationSchema),

    defaultValues: {
      password: "",
      repeatPassword: ""
    }
  });

  const { handleSubmit, reset } = methods;

  const [resetPassword, { loading }] = useResetPasswordMutation({
    onError({ message }) {
      if (message.toLowerCase() === FORBIDDEN_EXCEPTION) {
        return Alert.error(TOKEN_INVALID)
      }
    },

    onCompleted(data) {
      if (data) {
        const { resetPassword: { response } } = data

        if (response) {
          const { status } = response

          if (status === 404) {
            return Alert.error(RESET_PASSWORD_FAILURE);
          }

          if (status === 200) {
            reset();
            Alert.success(RESET_PASSWORD_SUCCESS)
            navigate(ROOT_ROUTE)
          }
        }
      }
    }
  });

  useEffect(() => {
    localStorage.getItem(AUTH_TOKEN) && navigate(ROOT_ROUTE)
    // eslint-disable-next-line
  }, []);

  const onSubmit: SubmitHandler<SetPasswordInput> = async (data: SetPasswordInput) => {
    if (token) {
      await resetPassword({
        variables: {
          resetPasswordInput: {
            password: data.password,
            token: token
          }
        }
      });
    }
    else {
      Alert.error(FOLLOW_INSTRUCTIONS)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        {SET_PASSWORD_FIELDS.map((field, index) => {
          const { fieldType, name, title } = field

          return (
            <CustomController
              key={index}
              controllerName={name}
              controllerLabel={title}
              fieldType={fieldType}
            />
          )
        })}

        <Box marginBottom='20px'>
          <Typography component={Link} to={AUTH_LINKS.LOGIN_LINK} color="primary" variant="subtitle1" sx={{ textDecoration: 'underline' }}>
            {BACK_TO_LOGIN}
          </Typography>
        </Box>

        <Button variant="contained" type="submit" fullWidth color="primary" disabled={loading} aria-label={RESET_PASSWORD}
          endIcon={loading && <CircularProgress size={20} color="inherit" />}>
          {RESET_PASSWORD}
        </Button>
      </form>
    </FormProvider>
  )
} 